import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles.module.scss';
import { FormData } from './types';
import { submitLead } from './utils/api';
import { getUTMParams, splitFullName } from './utils/helpers';
import {
  trackEnpalSourceCookie,
  trackLeadCreated,
  trackSlideAnswer,
  trackSliderFailure,
  trackSliderSuccess,
} from './utils/tracking';
import {
  bonificoBand,
  formatEur,
  quote,
  remainingMax,
  zoneFromCap,
} from './config/valuation';

const PRIVACY_URL = 'https://www.enpal.com/it/protezione-dei-dati';
const ENPAL_LOGO =
  'https://cdn.prod.website-files.com/65ba6cff483340071a459f9a/65ba6cff483340071a45ad60_63048f30262a7358bfb4aec4_enpal_logo_2k21_w-2.webp';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Poppins:wght@600&display=swap';

const CAP_PLACEHOLDERS = ['2', '0', '1', '2', '?'];

const BOILERS: { value: string; label: string; muted?: boolean }[] = [
  { value: 'gas', label: 'Gas' },
  { value: 'gasolio', label: 'Gasolio o GPL' },
  { value: 'altro', label: 'Altro' },
];

const SIZES = [
  { value: 'fino a 90', number: 'fino a 90', summary: 'fino a 90 mq' },
  { value: '90-150', number: '90-150', summary: '90-150 mq' },
  { value: 'sopra 150', number: 'sopra 150', summary: 'sopra 150 mq' },
];

const TERMS = [
  { value: 'radiatori', label: 'Termosifoni', api: 'termosifoni' },
  { value: 'pavimento', label: 'Pavimento', api: 'pavimento' },
  { value: 'ventole', label: 'Ventole', api: 'ventole' },
];

const WHENS = [
  { value: '1', label: '1-2 mesi', api: 'one_to_two_months' },
  { value: '2', label: '3-4 mesi', api: 'three_to_four_months' },
  { value: '3', label: 'Più avanti', api: 'more_than_5_months' },
];

const FVS: { value: string; label: string; muted?: boolean }[] = [
  { value: 'voglio', label: 'Sì, voglio il fotovoltaico' },
  { value: 'no', label: 'No, grazie' },
];

const NEXT_STEPS = [
  ['1', 'Ti chiamiamo entro 24 ore', 'Dieci minuti per confermare la cifra e metterla per iscritto.'],
  ['2', 'Sopralluogo gratuito', 'Un tecnico Enpal verifica impianto e misure a casa tua.'],
  ['3', 'Ritiro e installazione', 'Portiamo via la vecchia caldaia e installiamo la pompa di calore.'],
];

const BOILER_SUMMARY: Record<string, string> = {
  gas: 'Gas',
  gasolio: 'Gasolio o GPL',
  altro: 'Altra caldaia',
};

type Screen = 1 | 2 | 3 | 4;
type FocusField = 'nome' | 'mail' | 'tel' | null;
type Modo = 'permuta' | 'bonifico';

function okNome(value: string): boolean {
  return value.trim().replace(/\s+/g, ' ').length > 2;
}

function okMail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
}

function okTel(value: string): boolean {
  return /^3\d{9}$/.test(value);
}

function formatTel(value: string): string {
  if (value.length <= 3) return value;
  return value.slice(0, 3) + ' ' + value.slice(3);
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z"
        fill="none"
        stroke="#0DAF78"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 12l2 2 4-4"
        fill="none"
        stroke="#0DAF78"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg className={styles.arrow} width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M5 12h14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13 6l6 6-6 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Choice({
  selected,
  value,
  muted,
  short,
  tall,
  onClick,
  children,
}: {
  selected: boolean;
  value: string;
  muted?: boolean;
  short?: boolean;
  tall?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const className = [
    styles.opt,
    short ? styles.optShort : '',
    tall ? styles.optTall : '',
    selected ? styles.sel : '',
    muted && !selected ? styles.optMuted : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={className} data-choice={value} aria-pressed={selected} onClick={onClick}>
      {children}
    </button>
  );
}

const SliderCaldaiaMobile2: React.FC = () => {
  const [screen, setScreen] = useState<Screen>(1);
  const [cap, setCap] = useState<string[]>(['', '', '', '', '']);
  const [tipo, setTipo] = useState<string | null>(null);
  const [size, setSize] = useState<string | null>(null);
  const [term, setTerm] = useState<string | null>(null);
  const [quando, setQuando] = useState<string | null>(null);
  const [fv, setFv] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [mail, setMail] = useState('');
  const [tel, setTel] = useState('');
  const [terzi, setTerzi] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [modo, setModo] = useState<Modo | null>(null);
  const [offerteSeen, setOfferteSeen] = useState(false);
  const [consensoSeen, setConsensoSeen] = useState(false);
  const [focus, setFocus] = useState<FocusField>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const bodyRef = useRef<HTMLDivElement>(null);
  const capRefs = useRef<Array<HTMLInputElement | null>>([]);
  const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const liveIdRef = useRef<string | null>(null);
  const screenMark = useRef<Screen | null>(null);
  const capState = useRef(cap);
  const seen = useRef(new Set<string>());
  const submittingRef = useRef(false);
  capState.current = cap;

  const remember = useCallback((id: string, title: string, value: string) => {
    const key = id + ':' + value;
    if (seen.current.has(key)) return;
    seen.current.add(key);
    trackSlideAnswer(id, title, value);
  }, []);

  useEffect(() => {
    const id = 'slider-caldaia-mobile-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
    const utmParams = getUTMParams();
    trackEnpalSourceCookie(utmParams.utm_source || 'organic');
  }, []);

  useEffect(() => {
    bodyRef.current?.scrollTo(0, 0);
  }, [screen]);

  const capString = cap.join('');
  const zone = zoneFromCap(capString);
  const capComplete = capString.length === 5;

  const heatApi = TERMS.find((item) => item.value === term)?.api;
  const figures = useMemo(() => {
    if (!zone) return null;
    const valuation = quote(zone, heatApi, size || undefined);
    const ceiling = remainingMax(zone, size || undefined);
    const openBand = size === '90-150';
    const estimate =
      valuation.value === null ? ceiling : openBand ? valuation.high ?? ceiling : valuation.value;
    const payout =
      valuation.value !== null && valuation.high !== null
        ? bonificoBand(valuation.value, valuation.high).high
        : 0;
    return { estimate, openBand, permuta: valuation.high ?? 0, bonifico: payout };
  }, [zone, heatApi, size]);
  const permuta = figures?.permuta ?? 0;
  const bonifico = figures?.bonifico ?? 0;
  const telOk = okTel(tel);
  const nameOk = okNome(nome);
  const mailOk = okMail(mail);

  const showNameError = nome.trim() !== '' && !nameOk && focus !== 'nome';
  const showMailError = mail.trim() !== '' && !mailOk && focus !== 'mail';
  const showTelError = tel.trim() !== '' && !telOk && focus !== 'tel';

  const step1Ready = Boolean(zone && tipo && size);
  const step2Ready = Boolean(term && quando && fv);
  const step3Ready = nameOk && mailOk && telOk && Boolean(modo);
  const ctaDisabled =
    submitting ||
    (screen === 1 ? !step1Ready : screen === 2 ? !step2Ready : screen === 3 ? !step3Ready : false);

  const barValue =
    modo === 'bonifico' ? bonifico : modo === 'permuta' ? permuta : figures?.estimate ?? 0;
  const barAmount = formatEur(barValue);
  const barBlur = screen === 3 && !telOk;
  const estimateOpen = !heatApi || Boolean(figures?.openBand);
  let barLabel = estimateOpen ? 'La tua caldaia vale fino a' : 'La tua caldaia vale';
  let barSub = heatApi
    ? 'Dalla metratura e da come riscaldi casa'
    : 'Massimo possibile: scende e si precisa';
  if (modo === 'permuta') {
    barLabel = 'In permuta ricevi';
    barSub = telOk ? 'In sconto sulla nuova pompa di calore' : 'Cifra visibile dopo il cellulare';
  } else if (modo === 'bonifico') {
    barLabel = 'Dal GSE ricevi';
    barSub = telOk ? 'Incentivo versato in 2 rate, non è la permuta' : 'Cifra visibile dopo il cellulare';
  }

  const firstName = nome.trim().split(/\s+/)[0] || 'Ciao';
  const sizeSummary = SIZES.find((item) => item.value === size)?.summary || '';
  const summary = (BOILER_SUMMARY[tipo || ''] || 'Caldaia') + ' · CAP ' + capString + ' · ' + sizeSummary;
  const finalVal = modo === 'bonifico' ? formatEur(bonifico) : formatEur(permuta);

  const writeCap = (index: number, raw: string) => {
    const clean = raw.replace(/\D/g, '');
    const next = [...capState.current];
    if (!clean) {
      next[index] = '';
    } else {
      clean
        .split('')
        .slice(0, 5 - index)
        .forEach((digit, offset) => {
          next[index + offset] = digit;
        });
    }
    capState.current = next;
    setCap(next);
    const joined = next.join('');
    const last = clean ? Math.min(4, index + clean.length - 1) : index;
    if (clean && last < 4) capRefs.current[last + 1]?.focus();
    else if (clean && zoneFromCap(joined)) capRefs.current[4]?.blur();
    const nextZone = zoneFromCap(joined);
    if (nextZone) remember('location_form', 'Qual è il tuo CAP?', joined);
  };

  const titles: Record<Exclude<Screen, 4>, { title: string; dot: string; sub: string; cta: string }> = {
    1: {
      title: 'Quanto vale la tua caldaia',
      dot: '?',
      sub: 'Rispondi a tre domande e lo scopri subito.',
      cta: 'Scopri quanto vale',
    },
    2: { title: 'Ancora tre cose', dot: '', sub: 'Tre risposte e ci siamo.', cta: 'Avanti' },
    3: {
      title: 'A chi intestiamo la valutazione',
      dot: '?',
      sub: 'Te la confermiamo per iscritto in 24 ore.',
      cta: 'Conferma il valore',
    },
  };
  const heading =
    screen === 4
      ? {
          title: firstName + ', ci siamo',
          dot: '.',
          sub: 'Ti chiamiamo entro 24 ore per confermarti la cifra e metterla per iscritto. Dieci minuti.',
          cta: '',
        }
      : titles[screen];

  const submit = async () => {
    if (submittingRef.current || !step3Ready || !modo) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError('');
    remember('name_form', 'Nome e cognome', nome.trim());
    remember('email_form', 'Email', mail.trim());
    remember('phone_form', 'Cellulare', tel);

    const uid = uuidv4();
    const sizeApi = size || '';
    const heatApi = TERMS.find((item) => item.value === term)?.api || '';
    const whenApi = WHENS.find((item) => item.value === quando)?.api || '';
    const payload: FormData = {
      product_type: 'Heatpump',
      zip: capString,
      name: nome.trim(),
      email: mail.trim(),
      phone: tel,
      boiler_type: tipo || '',
      house_squared_meters: sizeApi,
      heating_source: heatApi,
      purchase_time: whenApi,
      fotovoltaico: fv || '',
      payout: modo === 'bonifico' ? 'wire' : 'trade',
    };

    try {
      const id = await submitLead(payload, uid, {
        enpalMarketingConsent: marketing,
        thirdPartyMarketing: terzi,
        thirdPartyProfiling: false,
      });
      if (id) {
        trackSliderSuccess();
        const { firstName: trackedFirst, lastName } = splitFullName(payload.name || '');
        window.setTimeout(() => {
          trackLeadCreated(payload.email || '', payload.phone || '', payload.zip || '', trackedFirst, lastName);
        }, 120);
        setScreen(4);
      } else {
        trackSliderFailure();
        setSubmitError('Non siamo riusciti a inviare la richiesta. Riprova.');
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      trackSliderFailure();
      setSubmitError('Non siamo riusciti a inviare la richiesta. Riprova.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const onCta = () => {
    if (ctaDisabled) return;
    if (screen < 3) {
      setScreen((current) => (current + 1) as Screen);
      return;
    }
    void submit();
  };

  const nomeSettled = nameOk && focus !== 'nome';
  const mailSettled = mailOk && focus !== 'mail';
  const telSettled = telOk && focus !== 'tel';

  const updateQuestionStates = () => {
    const steps =
      screen === 1
        ? ['cap', 'tipo', 'size']
        : screen === 2
          ? ['term', 'quando', 'fv']
          : screen === 3
            ? ['nome', 'mail', 'tel', 'modo', 'offerte', 'consenso']
            : [];
    const answered: Record<string, boolean> = {
      cap: Boolean(zone),
      tipo: Boolean(tipo),
      size: Boolean(size),
      term: Boolean(term),
      quando: Boolean(quando),
      fv: Boolean(fv),
      nome: nomeSettled,
      mail: mailSettled,
      tel: telSettled,
      modo: Boolean(modo),
      offerte: offerteSeen,
      consenso: consensoSeen,
    };
    const chosen: Record<string, string | null> = {
      tipo,
      size,
      term,
      quando,
      fv,
      modo,
    };
    let liveId: string | null = null;
    let blocked = false;
    steps.forEach((id) => {
      const el = stepRefs.current[id];
      if (!el) return;
      const hasAnswer = answered[id];
      let state: 'live' | 'done' | 'off' = 'off';
      if (!blocked && hasAnswer) state = 'done';
      else if (!blocked) {
        state = 'live';
        blocked = true;
        liveId = id;
      }
      const nextClass = styles[state];
      if (!el.classList.contains(nextClass)) {
        el.classList.remove(styles.live, styles.done, styles.off);
        el.classList.add(nextClass);
      }
      const focusable = el.querySelectorAll<HTMLElement>('button, input, a, [tabindex]');
      if (state === 'off') {
        el.setAttribute('inert', '');
        el.setAttribute('aria-disabled', 'true');
        focusable.forEach((node) => {
          if (!node.hasAttribute('data-tab-saved')) {
            node.setAttribute('data-tab-saved', node.getAttribute('tabindex') ?? '');
          }
          node.tabIndex = -1;
        });
      } else {
        el.removeAttribute('inert');
        el.removeAttribute('aria-disabled');
        focusable.forEach((node) => {
          if (!node.hasAttribute('data-tab-saved')) return;
          const saved = node.getAttribute('data-tab-saved') ?? '';
          if (saved) node.setAttribute('tabindex', saved);
          else node.removeAttribute('tabindex');
          node.removeAttribute('data-tab-saved');
        });
      }
      el.querySelectorAll<HTMLElement>('[data-choice]').forEach((button) => {
        button.classList.toggle(styles.sel, button.getAttribute('data-choice') === chosen[id]);
      });
    });

    const previous = liveIdRef.current;
    const changed = liveId !== previous;
    liveIdRef.current = liveId;
    if (!changed || !previous || !liveId) return;
    const el = stepRefs.current[liveId];
    const body = bodyRef.current;
    if (!el || !body) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) {
      el.classList.remove(styles.pop);
      void el.offsetWidth;
      el.classList.add(styles.pop);
      const onEnd = (event: AnimationEvent) => {
        if (event.target !== el) return;
        el.classList.remove(styles.pop);
        el.removeEventListener('animationend', onEnd);
      };
      el.addEventListener('animationend', onEnd);
    }
    const container = body.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    const visible = rect.top >= container.top - 1 && rect.bottom <= container.bottom + 1;
    if (!visible) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  useLayoutEffect(() => {
    if (screenMark.current !== screen) {
      screenMark.current = screen;
      liveIdRef.current = null;
    }
    updateQuestionStates();
  });

  const bindStep = (step: string) => (node: HTMLDivElement | null) => {
    stepRefs.current[step] = node;
  };

  return (
    <section className={styles.app}>
      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.wordmark}>
            noicompriamocaldaie<span className={styles.dotIt}>.it</span>
          </span>
          <span className={styles.powered}>
            powered by <img className={styles.logo} src={ENPAL_LOGO} alt="Enpal" />
          </span>
        </div>
        <h1 className={styles.h1}>
          {heading.title}
          {heading.dot ? <span className={styles.punct}>{heading.dot}</span> : null}
        </h1>
        {heading.sub ? <p className={styles.sub}>{heading.sub}</p> : null}
        {screen >= 1 && screen <= 3 ? (
          <div className={styles.progress} aria-hidden="true">
            {[1, 2, 3].map((step) => (
              <i key={step} className={`${styles.seg} ${screen >= step ? styles.segOn : ''}`} />
            ))}
          </div>
        ) : null}
      </header>

      {zone && screen >= 1 && screen <= 3 ? (
        <div className={styles.bar} aria-live="polite">
          <span className={styles.barText}>
            <span className={styles.barLabel}>{barLabel}</span>
            <span className={styles.barSub}>{barSub}</span>
          </span>
          <b className={`${styles.barAmount} ${barBlur ? styles.blur : ''}`} aria-hidden={barBlur}>
            {barAmount}
          </b>
        </div>
      ) : null}

      <div className={styles.body} ref={bodyRef}>
        {screen === 1 ? (
          <div className={styles.screen} key="step-1">
            <div className={styles.q} ref={bindStep('cap')}>
              <p className={styles.qt}>Qual è il tuo CAP?</p>
              <div className={styles.caps}>
                {cap.map((digit, index) => (
                  <input
                    key={index}
                    ref={(node) => {
                      capRefs.current[index] = node;
                    }}
                    className={CAP_PLACEHOLDERS[index] === '?' ? `${styles.cap} ${styles.capMark}` : styles.cap}
                    type="text"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={1}
                    aria-label={'Cifra ' + (index + 1) + ' del CAP'}
                    placeholder={CAP_PLACEHOLDERS[index]}
                    value={digit}
                    onChange={(event) => writeCap(index, event.target.value.replace(/\D/g, '').slice(-1))}
                    onPaste={(event) => {
                      event.preventDefault();
                      writeCap(index, event.clipboardData.getData('text'));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Backspace' && !capState.current[index] && index > 0) {
                        event.preventDefault();
                        const next = [...capState.current];
                        next[index - 1] = '';
                        capState.current = next;
                        setCap(next);
                        capRefs.current[index - 1]?.focus();
                      }
                    }}
                  />
                ))}
              </div>
              {capComplete && !zone ? (
                <p className={styles.capError}>Questo CAP non risulta in elenco.</p>
              ) : null}
            </div>

            <div className={styles.q} ref={bindStep('tipo')}>
              <p className={styles.qt}>Che caldaia hai?</p>
              <div className={styles.grid3}>
                {BOILERS.map((option) => (
                  <Choice
                    key={option.value}
                    value={option.value}
                    selected={tipo === option.value}
                    muted={option.muted}
                    onClick={() => {
                      setTipo(option.value);
                      remember('boiler_type', 'Che caldaia hai?', option.value);
                    }}
                  >
                    {option.label}
                  </Choice>
                ))}
              </div>
            </div>

            <div className={styles.q} ref={bindStep('size')}>
              <p className={styles.qt}>Quanto è grande casa tua?</p>
              <div className={styles.grid3}>
                {SIZES.map((option) => (
                  <Choice
                    key={option.value}
                    value={option.value}
                    tall
                    selected={size === option.value}
                    onClick={() => {
                      setSize(option.value);
                      remember('house_size', 'Quanto è grande casa tua?', option.value);
                    }}
                  >
                    <b className={styles.mqNum}>{option.number}</b>
                    <span className={styles.mqUnit}>mq</span>
                  </Choice>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {screen === 2 ? (
          <div className={styles.screen} key="step-2">
            <div className={styles.q} ref={bindStep('term')}>
              <p className={styles.qt}>Come riscaldi casa tua?</p>
              <div className={styles.grid3}>
                {TERMS.map((option) => (
                  <Choice
                    key={option.value}
                    value={option.value}
                    short
                    selected={term === option.value}
                    onClick={() => {
                      setTerm(option.value);
                      remember('heating_system', 'Come riscaldi casa tua?', option.api);
                    }}
                  >
                    {option.label}
                  </Choice>
                ))}
              </div>
            </div>

            <div className={styles.q} ref={bindStep('quando')}>
              <p className={styles.qt}>Entro quando vorresti sostituirla?</p>
              <div className={styles.grid3}>
                {WHENS.map((option) => (
                  <Choice
                    key={option.value}
                    value={option.value}
                    selected={quando === option.value}
                    onClick={() => {
                      setQuando(option.value);
                      remember('install_timeline', 'Entro quando vorresti sostituirla?', option.api);
                    }}
                  >
                    {option.label}
                  </Choice>
                ))}
              </div>
            </div>

            <div className={styles.q} ref={bindStep('fv')}>
              <p className={styles.qt}>Vuoi anche il fotovoltaico?</p>
              <div className={styles.grid2}>
                {FVS.map((option) => (
                  <Choice
                    key={option.value}
                    value={option.value}
                    selected={fv === option.value}
                    muted={option.muted}
                    onClick={() => {
                      setFv(option.value);
                      remember('fotovoltaico', 'Vuoi anche il fotovoltaico?', option.value);
                    }}
                  >
                    {option.label}
                  </Choice>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {screen === 3 ? (
          <div className={styles.screen} key="step-3">
            <div className={styles.field} ref={bindStep('nome')}>
              <label className={styles.label} htmlFor="caldaia-nome">
                Nome e cognome
              </label>
              <input
                id="caldaia-nome"
                className={`${styles.input} ${showNameError ? styles.inputBad : ''}`}
                type="text"
                autoComplete="name"
                placeholder="Come ti chiami"
                value={nome}
                onChange={(event) => setNome(event.target.value)}
                onFocus={() => setFocus('nome')}
                onBlur={() => setFocus(null)}
              />
              {showNameError ? <p className={styles.err}>Scrivi nome e cognome</p> : null}
            </div>

            <div
              className={styles.field}
              ref={bindStep('mail')}
            >
              <label className={styles.label} htmlFor="caldaia-mail">
                Email
              </label>
              <input
                id="caldaia-mail"
                className={`${styles.input} ${showMailError ? styles.inputBad : ''}`}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="nome@esempio.it"
                value={mail}
                onChange={(event) => setMail(event.target.value)}
                onFocus={() => setFocus('mail')}
                onBlur={() => setFocus(null)}
              />
              {showMailError ? <p className={styles.err}>Controlla l&apos;email: manca qualcosa</p> : null}
            </div>

            <div className={styles.field} ref={bindStep('tel')}>
              <label className={styles.label} htmlFor="caldaia-tel">
                Cellulare
              </label>
              <div className={`${styles.phone} ${showTelError ? styles.phoneBad : ''}`}>
                <span className={styles.prefix}>+39</span>
                <input
                  id="caldaia-tel"
                  className={styles.phoneInput}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  maxLength={11}
                  placeholder="333 1234567"
                  value={formatTel(tel)}
                  onChange={(event) => {
                    const next = event.target.value.replace(/\D/g, '').slice(0, 10);
                    setTel(next);
                    if (okTel(next)) event.target.blur();
                  }}
                  onFocus={() => setFocus('tel')}
                  onBlur={() => setFocus(null)}
                />
              </div>
              {showTelError ? <p className={styles.err}>Il cellulare ha 10 cifre e inizia con 3</p> : null}
              {!telOk ? <p className={styles.hint}>Inserisci il cellulare per vedere le cifre.</p> : null}
            </div>

            <div className={styles.q} ref={bindStep('modo')}>
            <p className={styles.payTitle}>Come vuoi ricevere l&apos;importo?</p>
            <div className={styles.modoGrid}>
              <button
                type="button"
                className={`${styles.modo} ${modo === 'bonifico' ? styles.sel : ''}`}
                data-choice="bonifico"
                aria-pressed={modo === 'bonifico'}
                onClick={() => {
                  setModo('bonifico');
                  remember('payout', "Come vuoi ricevere l'importo?", 'wire');
                }}
              >
                <b className={styles.modoName}>Bonifico GSE</b>
                <span className={styles.modoText}>
                  L&apos;incentivo te lo versa il GSE in 2 rate annuali. Pratiche a carico tuo.
                </span>
                <span
                  className={`${styles.modoVal} ${styles.modoValMuted} ${barBlur ? styles.blur : ''}`}
                  aria-hidden={barBlur}
                >
                  {formatEur(bonifico)}
                </span>
              </button>
              <button
                type="button"
                className={`${styles.modo} ${styles.modoBest} ${modo === 'permuta' ? styles.sel : ''}`}
                data-choice="permuta"
                aria-pressed={modo === 'permuta'}
                onClick={() => {
                  setModo('permuta');
                  remember('payout', "Come vuoi ricevere l'importo?", 'trade');
                }}
              >
                <span className={styles.pill}>CONSIGLIATA</span>
                <b className={styles.modoName}>Permuta</b>
                <span className={styles.modoText}>
                  La supervalutiamo e la scontiamo subito sulla nuova pompa di calore. Ritiro e pratiche a carico
                  nostro.
                </span>
                <span className={`${styles.modoVal} ${barBlur ? styles.blur : ''}`} aria-hidden={barBlur}>
                  {formatEur(permuta)}
                </span>
              </button>
            </div>
            </div>

            <div className={styles.q} ref={bindStep('offerte')}>
            <div
              className={`${styles.partner} ${terzi ? styles.partnerOn : ''}`}
              role="switch"
              aria-checked={terzi}
              tabIndex={0}
              onClick={() => {
                setTerzi((current) => !current);
                setOfferteSeen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setTerzi((current) => !current);
                  setOfferteSeen(true);
                }
              }}
            >
              <span className={styles.partnerCopy}>
                <b className={styles.partnerTitle}>Ricevi anche offerte dai partner</b>
                <span className={styles.partnerText}>
                  Comunichiamo i tuoi dati ai{' '}
                  <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                  >
                    partner in elenco
                  </a>
                  , titolari autonomi. Facoltativo, revocabile.
                </span>
              </span>
              <span className={`${styles.sw} ${terzi ? styles.swOn : ''}`}>
                <span className={`${styles.knob} ${terzi ? styles.knobOn : ''}`} />
              </span>
            </div>
            </div>

            <div className={styles.q} ref={bindStep('consenso')}>
            <label className={`${styles.checkRow} ${styles.checkRowOpt}`}>
              <input
                className={styles.box}
                type="checkbox"
                checked={marketing}
                onChange={(event) => {
                  setMarketing(event.target.checked);
                  setConsensoSeen(true);
                }}
              />
              <span>
                Acconsento a ricevere offerte e comunicazioni commerciali da Enpal.{' '}
                <i className={styles.optional}>Facoltativo</i>
              </span>
            </label>
            <p className={styles.callNote}>
              Ti chiameremo per conferma preventivo: la chiamata può essere registrata.{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
                Informativa privacy
              </a>
            </p>
            </div>
          </div>
        ) : null}

        {screen === 4 ? (
          <div className={styles.confirm}>
            <div className={styles.result} role="status">
              <p className={styles.summary}>{summary}</p>
              <h2 className={styles.resultTitle}>La tua caldaia vale</h2>
              <div className={styles.ticket}>
                <small className={styles.ticketLab}>{modo === 'bonifico' ? 'dal GSE' : 'in permuta'}</small>
                <b className={styles.ticketVal}>{finalVal}</b>
              </div>
              <p className={styles.why}>
                {modo === 'bonifico'
                  ? 'come incentivo Conto Termico, versato dal GSE in 2 rate. Confermato dopo il sopralluogo.'
                  : 'in sconto sulla nuova pompa di calore. Confermato dopo il sopralluogo.'}
              </p>
              <p className={styles.guarantee}>
                <ShieldIcon />
                Valore garantito da Enpal
              </p>
            </div>
            <div className={styles.next}>
              <span className={styles.nextKicker}>Cosa succede ora</span>
              {NEXT_STEPS.map(([number, title, text], index) => (
                <div key={number} className={styles.step}>
                  <div className={`${styles.stepNum} ${index === 0 ? styles.stepNumOn : styles.stepNumOff}`}>
                    {number}
                  </div>
                  <div className={styles.stepBody}>
                    <b className={styles.stepTitle}>{title}</b>
                    <span className={styles.stepText}>{text}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {screen < 4 ? (
        <footer className={styles.foot}>
          {submitError ? <p className={styles.footError}>{submitError}</p> : null}
          <button
            type="button"
            className={`${styles.cta} ${ctaDisabled ? '' : styles.ctaOn}`}
            disabled={ctaDisabled}
            onClick={onCta}
          >
            <span className={styles.shine} />
            <span className={styles.ctaLabel}>{submitting ? 'Invio…' : heading.cta}</span>
            <ArrowIcon />
          </button>
        </footer>
      ) : null}
    </section>
  );
};

export default SliderCaldaiaMobile2;
